import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { EngineGateService, EngineGateState } from 'projects/angular-clarity/src/app/services/engine-gate.service';
import * as fromRoot from 'projects/angular-clarity/src/app/stores/root.store';

/**
 * Full-page answer to the engine gate (KRAK-434): "your organisation is on the new version" (with the Kraken link
 * and a request to switch back) or "being switched" (a spinner that continues by itself). The root container renders
 * it INSTEAD of the toolbar, sidenav and router-outlet, because every API call is failing behind the gate and
 * anything else on screen would just show errors. Markup and styling follow the access-denied page.
 */
@Component({
    selector: 'app-engine-gate-page',
    templateUrl: './engine-gate-page.component.html',
    // The access-denied page's centred-column base, reused rather than copied, then this page's own refinements (a width-limited column,
    // spaced actions, small print).
    styleUrls: [
        '../../../modules/auth/components/access-denied/access-denied.component.scss',
        './engine-gate-page.component.scss',
    ],
})
export class EngineGatePageComponent implements OnInit, OnDestroy {
    /** The signed-in user, from the (never gated) session call. */
    upn = '';
    /** Needed for the remembered-request key; empty until the session loads. */
    mspId = '';

    state$ = this.gate.state$;
    switchBack$ = this.gate.switchBack$;

    private sub: Subscription;

    constructor(
        private gate: EngineGateService,
        private store: Store<fromRoot.State>,
    ) {}

    ngOnInit(): void {
        this.sub = this.store.select(fromRoot.selectSession).subscribe((state) => {
            this.upn = state?.session?.upn ?? '';
            this.mspId = state?.session?.msp_id ?? '';
            // A reload must show "Request sent", not the button again: read back this browser session's request.
            this.gate.restoreSwitchBack(this.mspId);
        });
    }

    ngOnDestroy(): void {
        this.sub?.unsubscribe();
    }

    /** "to the new version of Octiga" / "back to the classic version" for the switching heading. */
    directionText(state: EngineGateState): string {
        if (state?.kind !== 'switching') return '';
        if (state.direction === 'to_v2') return 'to the new version of Octiga';
        if (state.direction === 'to_v1') return 'back to the classic version';
        return '';
    }

    /** Leave the classic app for the new one (same tab). */
    openKraken(krakenUrl: string): void {
        window.location.assign(krakenUrl);
    }

    /** Send (or retry) the switch-back request. */
    requestSwitchBack(): void {
        this.gate.requestSwitchBack(this.mspId);
    }
}
